// "What happens by itself" (v1.83.0) — the visible-rules page the relay was
// waiting on: every automation touching this project, in plain sentences,
// DERIVED from the same config and stage data the automations read. Cards
// moving themselves demands trust; trust demands nothing ever feels like
// magic.
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '@/core/api/apiClient';
import { Icon } from '@/core/ui/icons';
import { Spinner } from '@/core/ui/atoms';
import { useEscapeToClose } from '@/core/ui/useEscapeToClose';

interface HouseRule { icon: string; text: string }

export function RulesPanel({ areaId, areaName, onClose }: { areaId: number; areaName: string; onClose: () => void }) {
  const [rules, setRules] = useState<HouseRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEscapeToClose(onClose);

  useEffect(() => {
    let dead = false;
    api.get<{ rules: HouseRule[] }>(`/areas/${areaId}/rules`)
      .then((r) => { if (!dead) setRules(r.rules); })
      .catch((e) => { if (!dead) setError(e instanceof Error ? e.message : 'Could not load the list.'); });
    return () => { dead = true; };
  }, [areaId]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl border border-line bg-canvas p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-[15px] font-semibold text-ink">What happens by itself — {areaName}</h2>
          <button onClick={onClose} className="rounded p-1 text-faint hover:text-ink" title="Close"><Icon i={X} size={14} /></button>
        </div>
        <p className="mb-4 text-[11px] text-faint">
          This list comes straight from the app’s own settings — it can’t say one thing and do another.
        </p>

        {error && <p className="text-[13px] text-danger">{error}</p>}
        {!error && !rules && <div className="flex justify-center py-8"><Spinner /></div>}

        {rules && (
          <ul className="space-y-3">
            {rules.map((r, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[13.5px] leading-relaxed text-ink">
                <span aria-hidden className="mt-px shrink-0">{r.icon}</span>
                <span>{r.text}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
